import type {
	Location,
	LocationId,
	Organization,
	OrganizationId,
	Provider,
} from "../../types";

export function updateOrganization(
	provider: Provider,
	orgId: OrganizationId,
	updater: (org: Organization) => Organization,
): Provider {
	return {
		...provider,
		organizations: provider.organizations.map((org) =>
			org.id === orgId ? updater(org) : org,
		),
	};
}

export function updateLocation(
	provider: Provider,
	orgId: OrganizationId,
	locId: LocationId,
	updater: (loc: Location) => Location,
): Provider {
	return updateOrganization(provider, orgId, (org) => ({
		...org,
		locations: org.locations.map((loc) =>
			loc.id === locId ? updater(loc) : loc,
		),
	}));
}

export function removeOrganization(
	provider: Provider,
	orgId: OrganizationId,
): Provider {
	return {
		...provider,
		organizations: provider.organizations.filter((org) => org.id !== orgId),
	};
}

export function removeLocation(
	provider: Provider,
	orgId: OrganizationId,
	locId: LocationId,
): Provider {
	return updateOrganization(provider, orgId, (org) => ({
		...org,
		locations: org.locations.filter((loc) => loc.id !== locId),
	}));
}

export function buildReferralText(args: {
	provider: Provider;
	organization: Organization;
	location: Location;
}): string {
	const { provider, organization, location } = args;
	const providerName = [
		provider.firstName,
		provider.middleName,
		provider.lastName,
		provider.suffix,
	]
		.filter(Boolean)
		.join(" ");

	const lines: string[] = [];

	lines.push(`${providerName}${provider.npi ? ` (NPI: ${provider.npi})` : ""}`);
	if (provider.specialty) lines.push(provider.specialty);
	lines.push("");

	const hasLinkedOrganization =
		organization.companyName !== "No Linked Organization";
	if (hasLinkedOrganization) {
		let orgLine = organization.companyName;
		if (organization.npi) orgLine += ` (NPI: ${organization.npi})`;
		lines.push(orgLine);
	}
	lines.push(location.addressLine1);
	if (location.addressLine2) lines.push(location.addressLine2);
	lines.push(`${location.city}, ${location.state} ${location.postalCode}`);

	lines.push("");
	lines.push(`Phone: ${location.phone || "Unknown"}`);
	lines.push(`Fax: ${location.fax || "Unknown"}`);

	return lines.join("\n");
}

export function validateProvider(provider: Provider): Record<string, string> {
	const errors: Record<string, string> = {};

	if (provider.organizations.length === 0) {
		errors["provider:organizations"] =
			"Provider must have at least one organization.";
	}

	provider.organizations.forEach((org) => {
		const orgKey = `org:${org.id}`;
		if (!org.companyName.trim()) {
			errors[`${orgKey}:companyName`] = "Organization name is required.";
		}

		if (org.locations.length === 0) {
			errors[`${orgKey}:locations`] =
				"Organization must have at least one location.";
		}

		org.locations.forEach((loc) => {
			const locKey = `loc:${org.id}:${loc.id}`;
			if (!loc.addressLine1.trim())
				errors[`${locKey}:addressLine1`] = "Address line 1 is required.";
			if (!loc.city.trim()) errors[`${locKey}:city`] = "City is required.";
			if (!loc.state.trim()) errors[`${locKey}:state`] = "State is required.";
			if (!loc.postalCode.trim())
				errors[`${locKey}:postalCode`] = "Postal code is required.";
		});
	});

	return errors;
}

export function getProviderFullName(provider: Provider): string {
	return [
		provider.firstName,
		provider.middleName,
		provider.lastName,
		provider.suffix,
	]
		.filter(Boolean)
		.join(" ");
}
